import { onBeforeUnmount, watchEffect } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';

export const SITE_NAME = 'Лесное дело';

export const DEFAULT_DESCRIPTION =
  'Товары и услуги деревообрабатывающих предприятий Центрального федерального округа: круглый лес, пиломатериалы, погонаж, мебель, тара, топливо.';

export interface SeoMeta {
  title: string;
  description?: string;
  /** Полный заголовок без приписки «— Лесное дело». */
  raw?: boolean;
  image?: string | null;
  /** noindex для служебных страниц: кабинет, админка, формы входа. */
  noindex?: boolean;
}

function upsertMeta(doc: Document, selector: string, attrs: Record<string, string>): void {
  let element = doc.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = doc.createElement('meta');
    doc.head.appendChild(element);
  }
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
}

function upsertLink(doc: Document, rel: string, href: string): void {
  let element = doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = doc.createElement('link');
    element.rel = rel;
    doc.head.appendChild(element);
  }
  element.href = href;
}

/**
 * Применяет мета-теги к документу. Вынесено из composable, чтобы вызывать и
 * из роутера (страницы без своих данных), и из компонентов (карточка, статья).
 */
export function applySeo(meta: SeoMeta, doc: Document = document): void {
  const title = meta.raw ? meta.title : `${meta.title} — ${SITE_NAME}`;
  const description = meta.description ?? DEFAULT_DESCRIPTION;
  const url = doc.defaultView ? (doc.defaultView.location.href.split('?')[0] ?? '') : '';

  doc.title = title;
  upsertMeta(doc, 'meta[name="description"]', { name: 'description', content: description });
  upsertMeta(doc, 'meta[property="og:title"]', { property: 'og:title', content: title });
  upsertMeta(doc, 'meta[property="og:description"]', {
    property: 'og:description',
    content: description,
  });
  upsertMeta(doc, 'meta[property="og:type"]', { property: 'og:type', content: 'website' });
  upsertMeta(doc, 'meta[property="og:site_name"]', {
    property: 'og:site_name',
    content: SITE_NAME,
  });
  if (url) {
    upsertMeta(doc, 'meta[property="og:url"]', { property: 'og:url', content: url });
    upsertLink(doc, 'canonical', url);
  }
  if (meta.image) {
    upsertMeta(doc, 'meta[property="og:image"]', { property: 'og:image', content: meta.image });
  } else {
    doc.head.querySelector('meta[property="og:image"]')?.remove();
  }
  upsertMeta(doc, 'meta[name="robots"]', {
    name: 'robots',
    content: meta.noindex ? 'noindex, nofollow' : 'index, follow',
  });
}

/** Реактивные мета-теги страницы: пересчитываются, когда загрузились данные. */
export function useSeo(meta: MaybeRefOrGetter<SeoMeta | null>): void {
  const stop = watchEffect(() => {
    const value = toValue(meta);
    if (value) applySeo(value);
  });
  onBeforeUnmount(stop);
}
